import { useEffect, useMemo, useState } from "react";
import { Search as SearchIcon, Plus, Pencil, ToggleLeft, ToggleRight, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const GST_OPTIONS = [0, 5, 18];
const EMPTY = { name: "", category: "", part_number: "", brand: "", make: "", model: "", gst_rate: 18, active: true };

export default function AdminSpareParts() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [make, setMake] = useState("");
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id,...}=edit
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    const [pr, cr] = await Promise.all([
      api.get("/admin/master-data/spare-parts", { params: { q: q || undefined, category: category || undefined, make: make || undefined } }).then((r) => r.data).catch(() => []),
      api.get("/admin/master-data/part-categories").then((r) => r.data).catch(() => []),
    ]);
    setRows(pr);
    setCats(cr);
    setLoading(false);
  };

  useEffect(() => { reload(); }, [category, make]);

  const activeCats = useMemo(() => cats.filter((c) => c.active !== false), [cats]);

  const openNew = () => { setForm({ ...EMPTY, category: activeCats[0]?.name || "" }); setEditing({}); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r); };
  const closeDialog = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Part name is required");
    if (!form.category) return toast.error("Please pick a category");
    setSaving(true);
    try {
      const body = { ...form, gst_rate: Number(form.gst_rate) };
      if (editing?.id) await api.patch(`/admin/master-data/spare-parts/${editing.id}`, body);
      else await api.post("/admin/master-data/spare-parts", body);
      toast.success(editing?.id ? "Part updated" : "Part created");
      closeDialog();
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/admin/master-data/spare-parts/${r.id}`, { ...r, active: !r.active });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/master-data/spare-parts/${r.id}`);
      toast.success("Part deleted");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-spare-parts-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Master Data</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557]">Spare Parts</h1>
          <p className="text-[13px] text-[#667085] mt-0.5">Standardised parts terminology used across all garages. Not an OEM catalogue.</p>
        </div>
        <Button onClick={openNew} data-testid="add-spare-part-btn" className="h-9 bg-[#e63946] hover:bg-[#c92e3b] text-white font-semibold text-[13px]">
          <Plus size={14} className="mr-1" /> Add Part
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-[#E4E7EC]">
        <div className="p-3 flex items-center gap-2 border-b border-[#F1FAEE] flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#667085]" />
            <input
              data-testid="sp-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") reload(); }}
              placeholder="Name, part number, brand…"
              className="pl-7 pr-3 h-9 rounded-md border border-[#E4E7EC] bg-white text-[13px] w-full focus:outline-none focus:border-[#457b9d]"
            />
          </div>
          <select data-testid="sp-cat-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-2 rounded-md border border-[#E4E7EC] bg-white text-[13px]">
            <option value="">All categories</option>
            {activeCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <input data-testid="sp-make-filter" value={make} onChange={(e) => setMake(e.target.value)} placeholder="Make filter" className="h-9 px-2.5 rounded-md border border-[#E4E7EC] bg-white text-[13px] w-[140px]" />
          <button onClick={reload} data-testid="sp-refresh" className="h-9 px-3 rounded-md bg-[#1d3557] text-white text-[12px] font-semibold hover:bg-[#152941]">Search</button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12px]">
            <thead className="bg-[#F7F9FC] text-left text-[#667085] uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-4 py-2">Part</th>
                <th className="px-4 py-2">Part #</th>
                <th className="px-4 py-2">Category</th>
                <th className="px-4 py-2">Brand</th>
                <th className="px-4 py-2">Make / Model</th>
                <th className="px-4 py-2 text-right">GST %</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1FAEE]">
              {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#667085]">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#667085]">
                  <Package size={20} className="mx-auto text-[#a8dadc] mb-1" />
                  No spare parts yet. Click <span className="font-semibold text-[#e63946]">Add Part</span> to seed the master.
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r.id} data-testid={`sp-row-${r.id}`} className="hover:bg-[#F7F9FC]">
                  <td className="px-4 py-2 font-semibold text-[#1d3557]">{r.name}</td>
                  <td className="px-4 py-2 font-mono text-[#667085]">{r.part_number || "—"}</td>
                  <td className="px-4 py-2">{r.category}</td>
                  <td className="px-4 py-2">{r.brand || "—"}</td>
                  <td className="px-4 py-2">{[r.make, r.model].filter(Boolean).join(" / ") || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono">{r.gst_rate}%</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${r.active ? "bg-[#12B76A]/10 text-[#027A48] border-[#12B76A]/25" : "bg-[#F7F9FC] text-[#667085] border-[#E4E7EC]"}`}>
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => openEdit(r)} data-testid={`sp-edit-${r.id}`} className="text-[#457b9d] hover:text-[#1d3557] p-1"><Pencil size={13} /></button>
                    <button onClick={() => toggleActive(r)} data-testid={`sp-toggle-${r.id}`} className="text-[#457b9d] hover:text-[#1d3557] p-1 ml-1">
                      {r.active ? <ToggleRight size={15} /> : <ToggleLeft size={15} />}
                    </button>
                    <button onClick={() => del(r)} data-testid={`sp-del-${r.id}`} className="text-[#e63946] hover:opacity-80 p-1 ml-1"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-lg" data-testid="sp-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Part" : "Add Part"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Part Name *</label>
              <Input data-testid="sp-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g., Front Brake Pad Set" className="h-9 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Category *</label>
                <select data-testid="sp-form-category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="w-full h-9 px-2 mt-1 rounded-md border border-[#E4E7EC] bg-white text-[13px]">
                  <option value="">— Select —</option>
                  {activeCats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">GST Rate</label>
                <select data-testid="sp-form-gst" value={form.gst_rate} onChange={(e) => setForm((f) => ({ ...f, gst_rate: Number(e.target.value) }))} className="w-full h-9 px-2 mt-1 rounded-md border border-[#E4E7EC] bg-white text-[13px]">
                  {GST_OPTIONS.map((g) => <option key={g} value={g}>{g}%</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Part Number</label>
                <Input data-testid="sp-form-part-number" value={form.part_number} onChange={(e) => setForm((f) => ({ ...f, part_number: e.target.value }))} className="h-9 mt-1 font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Brand</label>
                <Input data-testid="sp-form-brand" value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} className="h-9 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Make</label>
                <Input data-testid="sp-form-make" value={form.make} onChange={(e) => setForm((f) => ({ ...f, make: e.target.value }))} className="h-9 mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#667085] font-semibold">Model</label>
                <Input data-testid="sp-form-model" value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} className="h-9 mt-1" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-[13px] text-[#1d3557]">
              <input type="checkbox" data-testid="sp-form-active" checked={!!form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="sp-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="sp-save" className="bg-[#e63946] hover:bg-[#c92e3b] text-white">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
